import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { Marker, Popup, useMap } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import { toast } from '../../../services/toastService'
import { sightingAPI } from '../../../services/api'
import { getEntityMarkerIconV2 } from '../../../config/mapIcons'
import { ImageUpload } from '../../../components/ImageUpload'
import { usePopupCoordinator } from '../../../hooks/usePopupCoordinator'
import EntityPopupContent from '../../../components/map/EntityPopupContent'
import { createLogger } from '../../../utils/logger'
import { useMapAnalytics } from '../../../hooks/useMapAnalytics'

const logger = createLogger('EntityModeMarker')

/**
 * EntityModeMarker - Entity detail mode marker with inline confirmation
 * Allows users to confirm sightings with photos directly from the map
 */
const EntityModeMarker = memo(function EntityModeMarker({
  entity,
  user,
  navigate,
  onConfirmSuccess,
  onConfirmClick,
  isImmersiveMode,
  onTabChange,
  t,
  isSearchPanelMinimized,
  setIsSearchPanelMinimized,
  currentOpenPopupRef,
  onPanelStateChange,
  panelState,
  markerRefs
}) {
  const { currentLatitude: lat, currentLongitude: lng } = entity
  
  // All hooks must be called before any early returns
  const map = useMap()
  const [localConfirmationCount, setLocalConfirmationCount] = useState(entity.confirmationCount || 0)
  const markerRef = useRef(null)
  const { trackMarkerClick } = useMapAnalytics()
  
  // Use popup coordinator for state-driven coordination
  const { openPopup, closePopup, popupPadding } = usePopupCoordinator({
    panelState,
    onPanelStateChange
  })
  
  // Confirmation form state
  const [showConfirmForm, setShowConfirmForm] = useState(false)
  const [confirmImages, setConfirmImages] = useState([])
  const [confirmNotes, setConfirmNotes] = useState('')
  const [confirmEntityStatus, setConfirmEntityStatus] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update local confirmation count when entity prop changes
  useEffect(() => {
    setLocalConfirmationCount(entity.confirmationCount || 0)
  }, [entity.confirmationCount])

  // Store marker ref in markerRefs for programmatic access
  useEffect(() => {
    if (markerRef.current && markerRefs) {
      markerRefs.current[entity.id] = markerRef.current
    }
    return () => {
      if (markerRefs) {
        delete markerRefs.current[entity.id]
      }
    }
  }, [entity.id, markerRefs])

  const confirmationCount = localConfirmationCount
  const isUnconfirmed = confirmationCount === 0

  // Always use v2 brand-logo markers
  const markerIcon = useMemo(() => {
    return getEntityMarkerIconV2(entity, confirmationCount, entity.markerOpacity || 1.0)
  }, [confirmationCount, entity])

  // Force Leaflet to update the icon when it changes
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setIcon(markerIcon)
    }
  }, [markerIcon])

  // Check if user created the current sighting (API field: currentSightingUserId)
  const isOwnSighting = useMemo(() => {
    return !!(user && entity.currentSightingUserId && user.id === entity.currentSightingUserId)
  }, [user, entity.currentSightingUserId])
  
  // Define all callback handlers before early return
  const handleMarkerClick = useCallback(() => {
    // Track marker click
    trackMarkerClick(entity)

    // Close currently open popup
    if (currentOpenPopupRef?.current) {
      currentOpenPopupRef.current.closePopup()
    }
    
    // Center map on clicked marker in visible area
    if (map) {
      const mapSize = map.getSize()
      const markerPoint = map.latLngToContainerPoint([lat, lng])
      
      const controlPanelHeight = 80
      const visibleHeight = mapSize.y - controlPanelHeight
      const visibleCenterY = controlPanelHeight + (visibleHeight / 2)
      
      const targetX = mapSize.x / 2
      const targetY = visibleCenterY + 50
      
      const panX = targetX - markerPoint.x
      const panY = targetY - markerPoint.y
      
      map.panBy([panX, panY], {
        animate: true,
        duration: 0.5
      })
    }

    // Use coordinated popup opening
    if (markerRef.current) {
      openPopup(markerRef.current, { requireMinimizedPanel: true })
      
      if (currentOpenPopupRef) {
        currentOpenPopupRef.current = markerRef.current
      }
    }
  }, [map, lat, lng, openPopup, currentOpenPopupRef, entity, trackMarkerClick])

  // Close popup — used by EntityPopupContent when an action (e.g. Book) dismisses it
  const handleClosePopup = useCallback(() => {
    if (markerRef.current) markerRef.current.closePopup()
  }, [])

  const handleConfirm = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      toast.info(t('toast.info.loginToConfirm'))
      if (isImmersiveMode && onTabChange) {
        onTabChange('auth')
      } else {
        navigate('/login', { state: { from: window.location.pathname } })
      }
      return
    }

    if (isOwnSighting) {
      toast.info(t('toast.info.cannotConfirmOwn'))
      return
    }

    // Show confirmation form and minimize panel
    setShowConfirmForm(true)
    if (onPanelStateChange) {
      onPanelStateChange('minimized')
    }
  }, [user, navigate, isOwnSighting, isImmersiveMode, onTabChange, onPanelStateChange, t])

  const handleSubmitConfirmation = useCallback(async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (confirmImages.length === 0) {
      toast.error(t('toast.error.imageRequired'))
      return
    }

    setIsSubmitting(true)
    
    let uploadedImageUrl = null
    
    try {
      // Upload image first
      const formData = new FormData()
      formData.append('image', confirmImages[0])
      
      const uploadResponse = await sightingAPI.uploadImage(formData)
      uploadedImageUrl = uploadResponse.data?.url || uploadResponse.url

      // Submit confirmation with image
      await sightingAPI.confirmSightingWithImage(entity.id, {
        imageUrl: uploadedImageUrl,
        notes: confirmNotes.trim() || undefined,
        ...(confirmEntityStatus ? { entity_status: confirmEntityStatus } : {})
      })

      // Update local count
      setLocalConfirmationCount(prev => prev + 1)
      
      // Reset form
      setShowConfirmForm(false)
      setConfirmImages([])
      setConfirmNotes('')
      setConfirmEntityStatus(null)
      
      // Restore panel
      if (onPanelStateChange) {
        onPanelStateChange('semi')
      }
      
      toast.success(t('toast.success.sightingSubmitted'))
      
      // Callback for parent
      if (onConfirmSuccess) {
        onConfirmSuccess()
      }
    } catch (error) {
      logger.error('Confirmation failed', {
        error,
        errorResponse: error.response?.data,
        sentData: { imageUrl: uploadedImageUrl, notes: confirmNotes.trim() || undefined },
        sightingId: entity.id
      })
      toast.error(error.response?.data?.error || t('toast.error.confirmationFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }, [confirmImages, confirmNotes, confirmEntityStatus, entity.id, onConfirmSuccess, onPanelStateChange, t])

  const handleCancelConfirmation = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowConfirmForm(false)
    setConfirmImages([])
    setConfirmNotes('')
    setConfirmEntityStatus(null)
    // Restore panel when canceling
    if (onPanelStateChange) {
      onPanelStateChange('semi')
    }
  }, [onPanelStateChange])

  // Early return after all hooks have been called
  if (!lat || !lng) return null

  return (
    <Marker
      ref={markerRef}
      position={[lat, lng]}
      icon={markerIcon}
      eventHandlers={{
        click: (e) => {
          e.target.closePopup()
          handleMarkerClick()
        }
      }}
    >
      <Popup 
        key={`popup-${entity.id}-${panelState}`}
        className="custom-popup draggable-popup" 
        autoClose={false} 
        closeOnClick={false}
        autoPan={true}
      >
        <EntityPopupContent
          entity={entity}
          variant="entity"
          confirmationCount={confirmationCount}
          isEntityMode={true}
          lat={lat}
          lng={lng}
          sightingId={entity.id}
          isOwnSighting={isOwnSighting}
          showConfirmForm={showConfirmForm}
          handleConfirm={handleConfirm}
          handleSubmitConfirmation={handleSubmitConfirmation}
          handleCancelConfirmation={handleCancelConfirmation}
          confirmImages={confirmImages}
          confirmNotes={confirmNotes}
          setConfirmNotes={setConfirmNotes}
          confirmEntityStatus={confirmEntityStatus}
          setConfirmEntityStatus={setConfirmEntityStatus}
          isSubmitting={isSubmitting}
          ImageUpload={ImageUpload}
          setConfirmImages={setConfirmImages}
          user={user}
          userLocation={user?.location}
          isImmersiveMode={isImmersiveMode}
          onTabChange={onTabChange}
          onClosePopup={handleClosePopup}
        />
      </Popup>
    </Marker>
  )
})

export default EntityModeMarker
